import api from 'api/api';

const base = '/v2/loan-processing-batches';

export async function fetchLoanProcessingBatches(params = {}) {
  const q = new URLSearchParams();
  if (params.queue) q.set('queue', params.queue);
  if (params.status) q.set('status', params.status);
  if (params.page != null && params.page !== '') q.set('page', String(params.page));
  if (params.pageSize != null && params.pageSize !== '') q.set('pageSize', String(params.pageSize));
  const qs = q.toString();
  const res = await api.get({ path: `${base}${qs ? `?${qs}` : ''}` });
  const items = Array.isArray(res?.items) ? res.items : [];
  const paged = params.page != null && params.page !== '' && params.pageSize != null && params.pageSize !== '';
  if (paged) {
    return {
      items,
      total: typeof res?.total === 'number' ? res.total : 0,
      page: typeof res?.page === 'number' ? res.page : Number(params.page) || 1,
      pageSize: typeof res?.pageSize === 'number' ? res.pageSize : Number(params.pageSize) || items.length,
    };
  }
  return items;
}

/** Paginated Batch Processing view: batches for accessible programs with full application rows per member. */
export async function patchLoanProcessingBatch(batchId, body) {
  return api.patch({ path: `${base}/${batchId}`, body });
}

export async function fetchLoanProcessingBatchesManagePage(params = {}) {
  const q = new URLSearchParams();
  if (params.page != null) q.set('page', String(params.page));
  if (params.pageSize != null) q.set('pageSize', String(params.pageSize));
  if (params.sortBy != null && params.sortBy !== '') q.set('sortBy', String(params.sortBy));
  if (params.sortDir != null && params.sortDir !== '') q.set('sortDir', String(params.sortDir));
  if (params.fundingProgramId != null && params.fundingProgramId !== '') {
    q.set('fundingProgramId', String(params.fundingProgramId));
  }
  if (params.status != null && params.status !== '') q.set('status', String(params.status));
  if (params.createdById != null && params.createdById !== '') {
    q.set('createdById', String(params.createdById));
  }
  if (params.search != null && params.search !== '') q.set('search', String(params.search));
  const qs = q.toString();
  return api.get({ path: `${base}/manage-page${qs ? `?${qs}` : ''}` });
}

export async function createLoanProcessingBatch(body) {
  return api.post({ path: base, body });
}

/** Deletes a batch only when it has no members (server-enforced). */
export async function deleteLoanProcessingBatch(batchId) {
  return api.delete({ path: `${base}/${batchId}` });
}

export async function addApplicationToBatch(batchId, applicationId) {
  return api.post({ path: `${base}/${batchId}/members`, body: { applicationId } });
}

/** Records RF / C on the application (display: Removed from Batch / Completed) and removes it from the batch (batch must keep ≥1 loan). */
export async function withdrawApplicationFromBatch(batchId, applicationId, note) {
  const trimmed = note != null && String(note).trim() !== '' ? String(note).trim() : undefined;
  return api.delete({
    path: `${base}/${batchId}/members/${applicationId}`,
    body: trimmed ? { note: trimmed } : {},
  });
}

/**
 * Resets the batch (all applications removed; RF / C on every member — display: Removed from Batch / Completed;
 * all members removed; batch status set back to DRAFT).
 */
export async function resetLoanProcessingBatch(batchId, note) {
  const trimmed = note != null && String(note).trim() !== '' ? String(note).trim() : undefined;
  return api.post({
    path: `${base}/${batchId}/reset`,
    body: trimmed ? { note: trimmed } : {},
  });
}

export async function submitLoanProcessingBatch(batchId) {
  return api.post({ path: `${base}/${batchId}/submit`, body: {} });
}

export async function approveBatchL1(batchId) {
  return api.post({ path: `${base}/${batchId}/approve-l1`, body: {} });
}

export async function approveBatchL2(batchId) {
  return api.post({ path: `${base}/${batchId}/approve-l2`, body: {} });
}

export async function approveBatchOutgoingReceipt(batchId) {
  return api.post({ path: `${base}/${batchId}/approve-outgoing-receipt`, body: {} });
}

export async function approveBatchTrusteeSend(batchId) {
  return api.post({ path: `${base}/${batchId}/approve-trustee-send`, body: {} });
}

export async function uploadBatchFundingMemo(batchId, fileBase64, fileName) {
  return api.post({
    path: `${base}/${batchId}/funding-memo`,
    body: { fileBase64, fileName },
  });
}

export async function fetchBatchStepDocuments(batchId, step) {
  const q = new URLSearchParams();
  if (step) q.set('step', String(step));
  const qs = q.toString();
  return api.get({ path: `${base}/${batchId}/documents${qs ? `?${qs}` : ''}` });
}

export async function uploadBatchStepDocument(batchId, { step, fileBase64, fileName, fileType }) {
  return api.post({
    path: `${base}/${batchId}/documents`,
    body: { step, fileBase64, fileName, fileType },
  });
}

export async function deleteBatchStepDocument(batchId, documentId) {
  return api.delete({ path: `${base}/${batchId}/documents/${documentId}` });
}

export async function fetchBatchStepDocumentDownloadUrl(batchId, documentId) {
  return api.get({ path: `${base}/${batchId}/documents/${documentId}/download-url` });
}

export async function fetchBatchStepDocumentPreviewBase64(batchId, documentId) {
  return api.get({ path: `${base}/${batchId}/documents/${documentId}/preview-base64` });
}

export async function fetchBatchFundingMemoDownloadUrl(batchId) {
  return api.get({ path: `${base}/${batchId}/funding-memo/download-url` });
}

/** Base64 PDF for in-app preview (avoids raw binary fetch issues with some API hosts). */
export async function fetchBatchFundingMemoPreviewBase64(batchId) {
  return api.get({ path: `${base}/${batchId}/funding-memo/preview-base64` });
}

export async function fetchBatchSigningSheetDownloadUrl(batchId) {
  return api.get({ path: `${base}/${batchId}/signing-sheet/download-url` });
}

export async function fetchBatchInitialsLogDownloadUrl(batchId) {
  return api.get({ path: `${base}/${batchId}/initials-log-pdf/download-url` });
}

export function normalizeSignedDownloadUrl(payload) {
  if (typeof payload === 'string') return payload;
  if (payload && typeof payload.data === 'string') return payload.data;
  return null;
}

/**
 * Call directly from a click handler. Opens a tab synchronously, then sets location after the
 * signed URL fetch so popup blockers do not drop the window (async window.open often does nothing).
 */
export function openLoanBatchSignedDocumentInNewTab(batchId, fetchDownloadUrlFn) {
  const popup = window.open('about:blank', '_blank');
  void (async () => {
    try {
      const raw = await fetchDownloadUrlFn(batchId);
      const href = normalizeSignedDownloadUrl(raw);
      if (!href) {
        popup?.close();
        window.alert('No download URL returned.');
        return;
      }
      if (popup) {
        popup.location.href = href;
      } else {
        window.location.assign(href);
      }
    } catch (err) {
      popup?.close();
      window.alert(err?.message || 'Download failed');
    }
  })();
}

export function openLoanBatchStepDocumentInNewTab(batchId, documentId) {
  const popup = window.open('about:blank', '_blank');
  void (async () => {
    try {
      const raw = await fetchBatchStepDocumentDownloadUrl(batchId, documentId);
      const href = normalizeSignedDownloadUrl(raw);
      if (!href) {
        popup?.close();
        window.alert('No download URL returned.');
        return;
      }
      if (popup) {
        popup.location.href = href;
      } else {
        window.location.assign(href);
      }
    } catch (err) {
      popup?.close();
      window.alert(err?.message || 'Download failed');
    }
  })();
}

export async function fetchBatchSigningSheetBase64(batchId) {
  return api.get({ path: `${base}/${batchId}/signing-sheet` });
}

export async function fetchBatchInitialsLogBase64(batchId) {
  return api.get({ path: `${base}/${batchId}/initials-log-pdf` });
}
